// specificproduct.js

import React, { useState, useEffect, useContext } from "react";
import Dropdown2 from "./Dropdown2";
import "../style/Products.css";
import { useTranslation } from "react-i18next";
import { db } from "../firebase-config";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  CurrencyContext,
  pricePlusSurcharge,
} from "../context/CurrencyContext";

const SpecificProduct = () => {
  const { t, i18n } = useTranslation();
  const { category } = useParams();
  const navigate = useNavigate();

  // Brand states
  const [brands, setBrands] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { currency, exchangeRates } = useContext(CurrencyContext);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const q = query(
          collection(db, "categories"),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const fetchedCategories = querySnapshot.docs.map((doc) => doc.id);
        setCategories(fetchedCategories);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Failed to fetch categories. Please try again later.");
      }
    };

    fetchCategories();
  }, []);

  // Map URL path to category name and set initial selected categories
  useEffect(() => {
    if (category && categories.length > 0) {
      const categoryMap = categories.map((catName) => ({
        name: catName,
        path: catName.toLowerCase().replace(/\s+/g, "-"),
      }));

      const matchedCategory = categoryMap.find((cat) => cat.path === category);

      if (matchedCategory) {
        setSelectedCategories([matchedCategory.name]);
      } else {
        console.warn(`No matching category found for path: ${category}`);
        setSelectedCategories([]);
      }
    }
  }, [category, categories]);

  // Fetch all brands from all categories
  useEffect(() => {
    const fetchAllBrands = async () => {
      try {
        const brandMap = new Map();

        for (const category of categories) {
          const itemsCollection = collection(db, "products", category, "items");
          const itemsSnapshot = await getDocs(itemsCollection);

          itemsSnapshot.docs.forEach((itemDoc) => {
            const itemData = itemDoc.data();

            // Collect brand names
            if (itemData.brandName && itemData.brandName.trim() !== "") {
              if (!brandMap.has(itemData.brandName)) {
                brandMap.set(itemData.brandName, {
                  name: itemData.brandName,
                });
              }
            }
          });
        }

        setBrands(Array.from(brandMap.values()));
        setAvailableBrands(["Select All", ...Array.from(brandMap.keys())]);
      } catch (err) {
        console.error("Error fetching brands:", err);
        setError("Failed to fetch brands. Please try again later.");
      }
    };

    if (categories.length > 0) {
      fetchAllBrands();
    }
  }, [categories]);

  // Fetch products from selected categories or all categories
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);

        const fetchedProducts = new Map();

        const categoriesToFetch =
          selectedCategories.length === 0 ||
          selectedCategories.includes("Select All")
            ? categories
            : selectedCategories;

        for (const selectedCategory of categoriesToFetch) {
          const itemsCollection = collection(
            db,
            "products",
            selectedCategory,
            "items"
          );
          const q = query(itemsCollection, orderBy("createdAt", "desc"));
          const productsSnapshot = await getDocs(q);

          productsSnapshot.forEach((doc) => {
            const itemData = doc.data();
            const createdAt = itemData.createdAt
              ? itemData.createdAt.toDate()
              : new Date(0);

            const categoryVisibilityObj = itemData.categories.find(
              (cat) => cat.category === selectedCategory
            );

            const visibility = categoryVisibilityObj
              ? categoryVisibilityObj.visibility
              : "All";

            if (visibility === "No") {
              return;
            }

            const productKey = itemData.uuid || itemData.productId || doc.id;

            if (fetchedProducts.has(productKey)) {
              const existingProduct = fetchedProducts.get(productKey);
              existingProduct.categories.push({
                category: selectedCategory,
                visibility: visibility,
              });
            } else {
              fetchedProducts.set(productKey, {
                id: doc.id,
                uuid: itemData.uuid || null,
                name: itemData.name || "Unknown Product",
                price: `£${itemData.price || "0"}`,
                displayImages: itemData.displayImages || [],
                imageUrl: itemData.displayImages?.[0] || "",
                colors: itemData.colors || [],
                brandName: itemData.brandName || "",
                categories: [
                  {
                    category: selectedCategory,
                    visibility: visibility,
                  },
                ],
                selectedProductId: itemData.productId || "",
                createdAt,
                newIn: itemData.newIn || "",
              });
            }
          });
        }

        const productsArray = Array.from(fetchedProducts.values()).sort(
          (a, b) => b.createdAt - a.createdAt
        );

        setProducts(productsArray);
        setFilteredProducts(productsArray);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to fetch products. Please try again later.");
        setIsLoading(false);
      }
    };

    if (categories.length > 0) {
      fetchProducts();
    }
  }, [selectedCategories, categories]);

  // Apply filters
  useEffect(() => {
    const applyFilters = () => {
      let filtered = products;

      // Apply visibility logic
      filtered = filtered.filter((product) => {
        const productVisibilities = product.categories.map(
          (cat) => cat.visibility
        );

        if (productVisibilities.every((vis) => vis === "No")) {
          return false;
        }

        if (productVisibilities.includes("All")) {
          return true;
        }

        if (productVisibilities.includes("Limited")) {
          if (
            selectedCategories.length === 0 ||
            selectedCategories.includes("Select All")
          ) {
            return false;
          }

          const productCategories = product.categories
            .filter((cat) => cat.visibility === "Limited")
            .map((cat) => cat.category);

          const isInSelectedCategory = selectedCategories.some((selectedCat) =>
            productCategories.includes(selectedCat)
          );

          return isInSelectedCategory;
        }

        return false;
      });

      // Apply category filter
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes("Select All")
      ) {
        filtered = filtered.filter((product) => {
          const productCategories = product.categories.map(
            (cat) => cat.category
          );
          return selectedCategories.some((selectedCat) =>
            productCategories.includes(selectedCat)
          );
        });
      }

      // Apply brand filter
      if (selectedBrands.length > 0 && !selectedBrands.includes("select_all")) {
        filtered = filtered.filter((product) => {
          return selectedBrands.includes(product.brandName);
        });
      }

      setFilteredProducts(filtered);
    };

    applyFilters();
  }, [selectedCategories, selectedBrands, products]);

  // Reset selectedBrands if they are no longer available
  useEffect(() => {
    if (selectedBrands.length > 0 && !selectedBrands.includes("select_all")) {
      const validSelectedBrands = selectedBrands.filter((brand) =>
        availableBrands.includes(brand)
      );
      if (validSelectedBrands.length !== selectedBrands.length) {
        setSelectedBrands(validSelectedBrands);
      }
    }
  }, [availableBrands, selectedBrands]);

  const handleProductClick = (product) => {
    window.scrollTo(0, 0);
    const categoryName = product.categories[0].category;

    const url = `/full-product?category=${encodeURIComponent(
      categoryName
    )}&productId=${encodeURIComponent(product.id)}`;

    window.location.href = url;
  };

  // Prepare category options with "Select All" as the first item
  const categoryOptions = ["Select All", ...categories].map((cat) => t(cat));

  // Prepare brand options as objects with value and label
  const brandOptions = availableBrands.map((brandName) => {
    if (brandName === "Select All") {
      return { value: "select_all", label: t("Tout") };
    } else {
      return {
        value: brandName,
        label: brandName,
      };
    }
  });

  // Prevent scrolling when overlay is visible
  useEffect(() => {
    const preventScroll = (e) => {
      e.preventDefault();
    };

    if (isOverlayVisible) {
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("wheel", preventScroll, { passive: false });
    } else {
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
    }

    return () => {
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
    };
  }, [isOverlayVisible]);

  return (
    <div className="specific-product-container">
      {isOverlayVisible && (
        <div
          className="invisible-overlay"
          onClick={() => setOverlayVisible(false)}
        ></div>
      )}
      <div className="dropdown-container">
        <Dropdown2
          label={t("Marques")}
          options={brandOptions}
          selectedOptions={selectedBrands}
          setSelectedOptions={setSelectedBrands}
          setOverlayVisible={setOverlayVisible}
        />
        <Dropdown2
          label={t("Catégories")}
          options={categoryOptions}
          selectedOptions={selectedCategories}
          setSelectedOptions={setSelectedCategories}
          setOverlayVisible={setOverlayVisible}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="products-grid-unique">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="product-wrapper-unique"
              onClick={() => handleProductClick(product)}
            >
              <div className="product-container-unique">
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="product-image-unique"
                />
              </div>
              <div className="product-details-unique">
                <div className="new-in-unique">
                  {product.newIn ? t("Remote Bespoke") : <span>&nbsp;</span>}
                </div>
                <div className="item-name-unique">{product.name}</div>
                <div className="price-unique">
                  {
                    pricePlusSurcharge(
                      +product.price.replace(/[^0-9.-]+/g, ""),
                      currency,
                      exchangeRates
                    ).formatted
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpecificProduct;
